// getUserMedia / getDisplayMedia helpers. Kept plain: no wrapping class, no
// state — callers own the returned MediaStream and its lifetime.

import { DEFAULT_SETTINGS, screenShareOptions } from "@breeze/core";

export interface CameraHandles {
  readonly stream: MediaStream;
  readonly audioTrack: MediaStreamTrack | null;
  readonly videoTrack: MediaStreamTrack | null;
}

export async function openCamera(opts: {
  readonly video: boolean;
  readonly audio: boolean;
  readonly facingMode?: "user" | "environment";
}): Promise<CameraHandles> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: opts.audio ? { echoCancellation: true, noiseSuppression: true } : false,
    video: opts.video
      ? {
          width: { ideal: DEFAULT_SETTINGS.video.maxWidth },
          height: { ideal: DEFAULT_SETTINGS.video.maxHeight },
          frameRate: { ideal: DEFAULT_SETTINGS.video.maxFramerate },
          facingMode: opts.facingMode ?? "user",
        }
      : false,
  });
  return {
    stream,
    audioTrack: stream.getAudioTracks()[0] ?? null,
    videoTrack: stream.getVideoTracks()[0] ?? null,
  };
}

/** A black video track that stands in for the camera while it is off. The
 * camera itself is closed (its light goes out); this keeps the published
 * sender and the stream holding a live video track, so turning the camera
 * back on is a track swap and nothing else in the call changes. It redraws
 * once a second so a receiver that joins later still gets a frame. */
export interface OffTrack {
  readonly track: MediaStreamTrack;
  stop(): void;
}

export function createOffTrack(): OffTrack {
  const canvas = document.createElement("canvas");
  canvas.width = 320;
  canvas.height = 180;
  const ctx = canvas.getContext("2d");
  const stream = canvas.captureStream(2);
  const track = stream.getVideoTracks()[0]!;
  let flip = false;
  const paint = (): void => {
    if (!ctx) return;
    flip = !flip;
    ctx.fillStyle = flip ? "#000000" : "#010101";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };
  paint();
  const timer = setInterval(() => {
    if (track.readyState === "ended") {
      clearInterval(timer);
      return;
    }
    paint();
  }, 1000);
  return {
    track,
    stop() {
      clearInterval(timer);
      track.stop();
    },
  };
}

export function supportsScreenShare(): boolean {
  return typeof navigator.mediaDevices?.getDisplayMedia === "function";
}

export async function openScreenShare(): Promise<MediaStream> {
  // `selfBrowserSurface` is newer than the DOM typings, so the options are cast.
  return navigator.mediaDevices.getDisplayMedia(screenShareOptions() as unknown as DisplayMediaStreamOptions);
}

/** Whether flipping between a front and back camera is even possible on this
 * device — used to decide whether the flip-camera control shows at all. */
export async function hasMultipleCameras(): Promise<boolean> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === "videoinput").length > 1;
  } catch {
    return false;
  }
}

export function stopStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((track) => track.stop());
}

/** Why a camera or mic could not be opened: the person (or the browser)
 * said no, there is no such device, or this browser has no camera API at
 * all (an app's built-in browser, most often). */
export type MediaProblem = "denied" | "none" | "unsupported";

export interface CallMedia extends CameraHandles {
  /** Set when the camera could not be opened. */
  readonly cameraProblem: MediaProblem | null;
  /** Set when the mic could not be opened. */
  readonly micProblem: MediaProblem | null;
}

function problemOf(err: unknown): MediaProblem {
  const name = (err as { name?: string } | null)?.name ?? "";
  if (name === "NotFoundError" || name === "OverconstrainedError" || name === "NotReadableError") return "none";
  return "denied";
}

/** Opens camera and mic together, so the browser asks once, and falls back
 * to whichever of the two it can get. Never throws: a call can still be
 * joined to see and hear the others with neither. Both tracks are always
 * requested, whatever the person's toggles say, so turning one on later is
 * a switch rather than a second permission prompt. */
export async function openCallMedia(facingMode: "user" | "environment" = "user"): Promise<CallMedia> {
  if (typeof navigator === "undefined" || typeof navigator.mediaDevices?.getUserMedia !== "function") {
    return { stream: new MediaStream(), audioTrack: null, videoTrack: null, cameraProblem: "unsupported", micProblem: "unsupported" };
  }
  try {
    const both = await openCamera({ video: true, audio: true, facingMode });
    return { ...both, cameraProblem: both.videoTrack ? null : "none", micProblem: both.audioTrack ? null : "none" };
  } catch (err) {
    const first = problemOf(err);
    // One of the two may still be reachable (a laptop with no camera, a
    // camera another app holds).
    let audio: CameraHandles | null = null;
    let video: CameraHandles | null = null;
    let micProblem: MediaProblem | null = null;
    let cameraProblem: MediaProblem | null = null;
    try {
      audio = await openCamera({ video: false, audio: true });
    } catch (e) {
      micProblem = first === "denied" ? "denied" : problemOf(e);
    }
    try {
      video = await openCamera({ video: true, audio: false, facingMode });
    } catch (e) {
      cameraProblem = first === "denied" ? "denied" : problemOf(e);
    }
    const stream = new MediaStream();
    const audioTrack = audio?.audioTrack ?? null;
    const videoTrack = video?.videoTrack ?? null;
    if (audioTrack) stream.addTrack(audioTrack);
    if (videoTrack) stream.addTrack(videoTrack);
    return { stream, audioTrack, videoTrack, cameraProblem, micProblem };
  }
}

/** One plain sentence for what went wrong, or null when nothing did. */
export function mediaProblemText(media: Pick<CallMedia, "cameraProblem" | "micProblem">): string | null {
  const { cameraProblem: cam, micProblem: mic } = media;
  if (cam === "unsupported" || mic === "unsupported") {
    return "This browser can't use your camera or mic. Open the link in Safari or Chrome.";
  }
  if (cam === "denied" && mic === "denied") {
    return "Camera and mic are blocked. Allow them for this site in your browser's settings, then try again.";
  }
  if (cam === "denied") return "Your camera is blocked. Allow it for this site in your browser's settings.";
  if (mic === "denied") return "Your mic is blocked. Allow it for this site in your browser's settings.";
  if (cam === "none" && mic === "none") return "No camera or mic found.";
  if (cam === "none") return "No camera found. You can still talk.";
  if (mic === "none") return "No mic found. Others will see you but not hear you.";
  return null;
}
